import { z } from "zod";

/*
 * Gerätekarte: Daten vom Typenschild (Heizung, Boiler, Wallbox, Waschmaschine …), schriftlich im Projektordner abgelegt.
 * Die Schrifterkennung läuft im Browser (Tesseract, selbst gehostet); sie ist nur ein Vorschlag, die Person prüft und ändert.
 */

const optional = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Dieser Text ist zu lang (höchstens ${max} Zeichen).`)
    .optional()
    .transform((v) => (v ? v : undefined));

export const deviceCardSchema = z.object({
  name: z.string().trim().min(2, "Bitte geben Sie dem Gerät einen Namen, zum Beispiel „Heizung Keller“.").max(120, "Der Name ist zu lang."),
  manufacturer: optional(120),
  model: optional(160),
  serial: optional(120),
  year: optional(20),
  power: optional(60),
  voltage: optional(60),
  place: optional(160),
  notes: optional(1500),
  /** Ende der Garantie (JJJJ-MM-TT), freiwillig. */
  warrantyUntil: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Bitte geben Sie ein gültiges Datum an.")
    .optional()
    .or(z.literal("").transform(() => undefined)),
  /** Wartung alle … Monate, freiwillig (0 oder leer = keine). */
  serviceMonths: z.number().int().min(0).max(120).optional(),
  /** Foto des Typenschilds, bereits im Ordner abgelegt. */
  photoFileId: z.uuid().optional(),
});
export type DeviceCardInput = z.infer<typeof deviceCardSchema>;

export const DEVICE_CARD_DISCLAIMER =
  "Die Angaben stammen vom Typenschild und wurden von der erfassenden Person geprüft. Maßgeblich ist das Typenschild am Gerät. Diese Karte ersetzt weder Prüfung noch Wartungsnachweis.";

export interface NameplateGuess {
  manufacturer?: string;
  model?: string;
  serial?: string;
  year?: string;
  power?: string;
  voltage?: string;
}

const BRANDS = [
  "Viessmann", "Vaillant", "Bosch", "Junkers", "Buderus", "Wolf", "Stiebel Eltron", "Siemens", "AEG", "Miele", "Bauknecht", "Hager", "ABB", "Schneider Electric",
  "Siedle", "Daikin", "Panasonic", "Samsung", "LG", "Wilo", "Grundfos", "Weishaupt", "Remko", "NIBE", "Kermi", "Zehnder", "Dimplex", "Clage", "Gira", "Jung",
  "Busch-Jaeger", "Merten", "Eltako", "Mennekes", "KOSTAL", "SMA", "Fronius", "Huawei", "Tesla", "go-e", "Heidelberg", "Easee", "KEBA", "Hoval", "Ariston",
  "Elco", "Brötje", "Oertli", "Rotex", "Saunier Duval", "Liebherr", "Electrolux", "Whirlpool", "Gorenje", "Beko", "Haier", "Dyson", "Philips", "Grohe", "Hansgrohe",
];

const clean = (s: string) => s.replace(/\s+/g, " ").replace(/^[\s:.\-–]+|[\s:.\-–]+$/g, "").trim();

/** Liest aus dem erkannten Text die wahrscheinlichen Angaben heraus. Vorsichtig: lieber leer lassen als falsch raten. */
export function parseNameplate(raw: string): NameplateGuess {
  const text = raw.replace(/\r/g, "");
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const out: NameplateGuess = {};

  const lower = text.toLowerCase();
  const brand = BRANDS.find((b) => lower.includes(b.toLowerCase()));
  if (brand) out.manufacturer = brand;

  const take = (re: RegExp): string | undefined => {
    for (const l of lines) {
      const m = re.exec(l);
      const v = m?.[1] ? clean(m[1]) : "";
      if (v.length >= 2) return v;
    }
    return undefined;
  };

  // Seriennummer: „Ser.-Nr.“, „Serial No.“, „S/N“, „Fabr.-Nr.“
  const serial = take(/(?:ser(?:ien)?[\s.\-]*(?:nr|nummer|no|number)\.?|serial(?:\s*(?:no|number|nr))?|s\/n|\bsn\b|fabr(?:ik)?[\s.\-]*(?:nr|nummer)\.?|fab\.?[\s\-]*nr\.?)\s*[:.#]?\s*([A-Z0-9][A-Z0-9\-\/. ]{3,30})/i);
  if (serial) out.serial = serial.split(/\s{2,}/)[0]!.slice(0, 40);

  // Typ/Modell
  const model = take(/^(?:typ|type|modell?|model|bezeichnung|art[.\-]?\s*(?:nr|no)\.?|artikel(?:nummer)?)\s*[:.#]?\s*(.{2,60})$/i);
  if (model) out.model = model;

  // Baujahr: „Baujahr 2019“, „BJ: 04/2018“, „Year 2020“, „Mfg. date 2021“
  const year = /(?:bau\s*jahr|baujahr|bj\.?|year|jahr|herst(?:ellungs)?[\s.\-]*(?:jahr|datum)|mfg\.?(?:\s*date)?|date)\s*[:.]?\s*(?:\d{1,2}[./\-])?((?:19|20)\d{2})/i.exec(text);
  if (year?.[1]) out.year = year[1];

  // Leistung und Spannung
  const power = /(\d{1,4}(?:[.,]\d{1,2})?(?:\s*[-–]\s*\d{1,4}(?:[.,]\d{1,2})?)?)\s*(kW|W)\b/.exec(text);
  if (power) out.power = `${power[1]!.replace(/,/g, ".").replace(/\s*[-–]\s*/, " – ")} ${power[2]}`;
  const volt = /(\d{2,3}(?:\s*[\/\-]\s*\d{3})?)\s*V\b/.exec(text);
  if (volt) out.voltage = `${volt[1]!.replace(/\s+/g, "")} V`;

  return out;
}


/** Eine erfasste Gerätekarte in der Liste „Geräte im Haus“. */
export interface DeviceListItem {
  id: string;
  fileId: string;
  projectId: string;
  projectName: string;
  folderId: string;
  name: string;
  manufacturer: string | null;
  model: string | null;
  serial: string | null;
  year: string | null;
  place: string | null;
  warrantyUntil: string | null;
  serviceMonths: number | null;
  createdAt: string;
}
